// AdminSupportManagement.jsx
// Lets an admin/admin_support user create and edit FAQ categories and
// questions directly from the site, instead of seeding via script.
//
// Talks to (all require auth + ADMIN/ADMIN_SUPPORT role — already
// enforced server-side by adminSupportRoutes.js):
//   GET   /admin/support/categories
//   POST  /admin/support/categories
//   PATCH /admin/support/categories/:id
//   GET   /admin/support/questions?categoryId=...   <- confirmed correct against
//                                                        getAdminSupportQuestions()
//   POST  /admin/support/questions
//   PATCH /admin/support/questions/:id
//   DELETE /admin/support/questions/:id
// via the shared `api` instance (src/services/api.js), which already
// prefixes REACT_APP_API_URL and attaches the auth token.

import { useState, useEffect } from 'react';
import {
  getAdminSupportCategories,
  createAdminSupportCategory,
  updateAdminSupportCategory,
  getAdminSupportQuestions,
  createAdminSupportQuestion,
  updateAdminSupportQuestion,
  deleteAdminSupportQuestion,
} from '../../services/Adminsupportservice'; // adjust path to match this file's location
import './AdminSupportManagement.css';

// Backend requires a unique `slug` per category (used in URLs) but the
// admin form only collects a display name — derive the slug from it so
// admins never have to think about URL formatting themselves.
const slugify = (str) =>
  str
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// SupportQuestion has @@unique([categoryId, displayOrder]) in the schema,
// so two questions in the same category can never share a displayOrder.
// Suggest the next free slot as (highest existing displayOrder) + 1 rather
// than questions.length + 1 — length-based guesses collide as soon as a
// question has been deleted, or reused a non-sequential number.
const nextDisplayOrder = (questionList) =>
  questionList.reduce((max, q) => Math.max(max, q.displayOrder ?? 0), 0) + 1;

// Pull a useful message out of whatever shape the api layer throws.
// Falls back to a generic message if the backend didn't send one.
const getErrorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

const emptyQuestionForm = {
  question: '',
  answer: '',
  displayOrder: 1,
  isActive: true,
  allowLiveSupport: true,
  contactEmail: '',
  contactPhone: '',
  resourceLink: '',
};

export default function AdminSupportManagement() {
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [questions, setQuestions] = useState([]);

  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', sortOrder: 1 });
  const [editingCategoryId, setEditingCategoryId] = useState(null);

  const [questionForm, setQuestionForm] = useState(emptyQuestionForm);
  const [editingQuestionId, setEditingQuestionId] = useState(null);

  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCategoryId) loadQuestions(selectedCategoryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryId]);

  async function loadCategories() {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getAdminSupportCategories();
      setCategories(data.categories || []);
      if (!selectedCategoryId && data.categories?.length) {
        setSelectedCategoryId(data.categories[0].id);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Could not load categories.'));
    } finally {
      setIsLoading(false);
    }
  }

  // Returns the freshly-loaded list so callers (like submitQuestion) can use
  // it immediately, instead of reading `questions` state before React has
  // re-rendered with the update.
  async function loadQuestions(categoryId) {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getAdminSupportQuestions(categoryId);
      const freshQuestions = data.questions || [];
      setQuestions(freshQuestions);
      return freshQuestions;
    } catch (err) {
      setError(getErrorMessage(err, 'Could not load questions for this category.'));
      return [];
    } finally {
      setIsLoading(false);
    }
  }

  // ---------- Category form ----------

  function startEditCategory(cat) {
    setEditingCategoryId(cat.id);
    setCategoryForm({
      name: cat.name,
      description: cat.description || '',
      sortOrder: cat.sortOrder ?? 1,
    });
  }

  function resetCategoryForm() {
    setEditingCategoryId(null);
    setCategoryForm({ name: '', description: '', sortOrder: categories.length + 1 });
  }

  async function submitCategory(e) {
    e.preventDefault();
    if (!categoryForm.name.trim()) return;

    setIsLoading(true);
    setError(null);
    try {
      const isEdit = !!editingCategoryId;
      if (isEdit) {
        await updateAdminSupportCategory(editingCategoryId, categoryForm);
      } else {
        await createAdminSupportCategory({
          ...categoryForm,
          slug: slugify(categoryForm.name),
        });
      }
      resetCategoryForm();
      await loadCategories();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save category.'));
    } finally {
      setIsLoading(false);
    }
  }

  async function toggleCategoryActive(cat) {
    try {
      await updateAdminSupportCategory(cat.id, { isActive: !cat.isActive });
      loadCategories();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not update category status.'));
    }
  }

  // ---------- Question form ----------

  function startEditQuestion(q) {
    setEditingQuestionId(q.id);
    setQuestionForm({
      question: q.question,
      answer: q.answer,
      displayOrder: q.displayOrder ?? 1,
      isActive: q.isActive ?? true,
      allowLiveSupport: q.allowLiveSupport ?? true,
      contactEmail: q.contactEmail || '',
      contactPhone: q.contactPhone || '',
      resourceLink: q.resourceLink || '',
    });
  }

  // Accepts the current question list explicitly so it can be called with
  // freshly-loaded data (see submitQuestion) instead of relying on
  // potentially-stale `questions` state.
  function resetQuestionForm(questionList = questions) {
    setEditingQuestionId(null);
    setQuestionForm({ ...emptyQuestionForm, displayOrder: nextDisplayOrder(questionList) });
  }

  async function submitQuestion(e) {
    e.preventDefault();
    if (!questionForm.question.trim() || !questionForm.answer.trim() || !selectedCategoryId) return;

    setIsLoading(true);
    setError(null);
    try {
      const isEdit = !!editingQuestionId;
      const body = isEdit ? questionForm : { ...questionForm, categoryId: selectedCategoryId };
      if (isEdit) {
        await updateAdminSupportQuestion(editingQuestionId, body);
      } else {
        await createAdminSupportQuestion(body);
      }
      // Reload FIRST so the next-displayOrder suggestion is based on what's
      // actually in the category now (including the question we just added),
      // not on the pre-submit count. Fixes duplicate-displayOrder collisions
      // against the categoryId+displayOrder unique constraint.
      const freshQuestions = await loadQuestions(selectedCategoryId);
      resetQuestionForm(freshQuestions);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save question.'));
    } finally {
      setIsLoading(false);
    }
  }

  async function deleteQuestion(id) {
    if (!window.confirm('Delete this question? This cannot be undone.')) return;
    try {
      await deleteAdminSupportQuestion(id);
      loadQuestions(selectedCategoryId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not delete question.'));
    }
  }

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);

  return (
    <div className="admin-support-page">
      <h1 className="admin-support-heading">Support Content Management</h1>
      {error && <div className="admin-support-error">{error}</div>}

      <div className="admin-support-layout">
        {/* ---------- Categories column ---------- */}
        <div className="admin-support-panel">
          <h2 className="admin-support-panel-title">Categories</h2>

          <ul className="admin-support-category-list">
            {categories.map((cat) => (
              <li
                key={cat.id}
                className={`admin-support-category-item ${
                  cat.id === selectedCategoryId ? 'admin-support-category-item--active' : ''
                }`}
              >
                <button
                  className="admin-support-category-select"
                  onClick={() => setSelectedCategoryId(cat.id)}
                >
                  {cat.name}
                  {!cat.isActive && <span className="admin-support-badge">inactive</span>}
                </button>
                <div className="admin-support-category-actions">
                  <button onClick={() => startEditCategory(cat)}>Edit</button>
                  <button onClick={() => toggleCategoryActive(cat)}>
                    {cat.isActive ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </li>
            ))}
          </ul>

          <form className="admin-support-form" onSubmit={submitCategory}>
            <h3>{editingCategoryId ? 'Edit Category' : 'New Category'}</h3>
            <input
              type="text"
              placeholder="Category name"
              value={categoryForm.name}
              onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
              required
            />
            <textarea
              placeholder="Description (optional)"
              value={categoryForm.description}
              onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
              rows={2}
            />
            <input
              type="number"
              placeholder="Display order"
              value={categoryForm.sortOrder}
              onChange={(e) =>
                setCategoryForm({ ...categoryForm, sortOrder: Number(e.target.value) })
              }
            />
            <div className="admin-support-form-actions">
              <button type="submit" className="admin-support-primary-btn">
                {editingCategoryId ? 'Save Changes' : 'Add Category'}
              </button>
              {editingCategoryId && (
                <button type="button" onClick={resetCategoryForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* ---------- Questions column ---------- */}
        <div className="admin-support-panel">
          <h2 className="admin-support-panel-title">
            Questions {selectedCategory ? `— ${selectedCategory.name}` : ''}
          </h2>

          {!selectedCategoryId && <p>Select a category to manage its questions.</p>}

          {selectedCategoryId && (
            <>
              <ul className="admin-support-question-list">
                {questions.map((q) => (
                  <li key={q.id} className="admin-support-question-item">
                    <div>
                      <strong>{q.question}</strong>
                      {!q.isActive && <span className="admin-support-badge">inactive</span>}
                      <p className="admin-support-question-answer">{q.answer}</p>
                    </div>
                    <div className="admin-support-question-actions">
                      <button onClick={() => startEditQuestion(q)}>Edit</button>
                      <button onClick={() => deleteQuestion(q.id)}>Delete</button>
                    </div>
                  </li>
                ))}
              </ul>

              <form className="admin-support-form" onSubmit={submitQuestion}>
                <h3>{editingQuestionId ? 'Edit Question' : 'New Question'}</h3>
                <input
                  type="text"
                  placeholder="Question"
                  value={questionForm.question}
                  onChange={(e) => setQuestionForm({ ...questionForm, question: e.target.value })}
                  required
                />
                <textarea
                  placeholder="Answer"
                  value={questionForm.answer}
                  onChange={(e) => setQuestionForm({ ...questionForm, answer: e.target.value })}
                  rows={3}
                  required
                />
                <input
                  type="number"
                  placeholder="Display order"
                  value={questionForm.displayOrder}
                  onChange={(e) =>
                    setQuestionForm({ ...questionForm, displayOrder: Number(e.target.value) })
                  }
                />
                <input
                  type="url"
                  placeholder="Resource link (optional)"
                  value={questionForm.resourceLink}
                  onChange={(e) =>
                    setQuestionForm({ ...questionForm, resourceLink: e.target.value })
                  }
                />
                <input
                  type="email"
                  placeholder="Contact email (optional)"
                  value={questionForm.contactEmail}
                  onChange={(e) =>
                    setQuestionForm({ ...questionForm, contactEmail: e.target.value })
                  }
                />
                <input
                  type="text"
                  placeholder="Contact phone (optional)"
                  value={questionForm.contactPhone}
                  onChange={(e) =>
                    setQuestionForm({ ...questionForm, contactPhone: e.target.value })
                  }
                />
                <label className="admin-support-checkbox-label">
                  <input
                    type="checkbox"
                    checked={questionForm.allowLiveSupport}
                    onChange={(e) =>
                      setQuestionForm({ ...questionForm, allowLiveSupport: e.target.checked })
                    }
                  />
                  Allow escalation to a support ticket from this question
                </label>

                <div className="admin-support-form-actions">
                  <button type="submit" className="admin-support-primary-btn">
                    {editingQuestionId ? 'Save Changes' : 'Add Question'}
                  </button>
                  {editingQuestionId && (
                    <button type="button" onClick={() => resetQuestionForm()}>
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      {isLoading && <div className="admin-support-loading">Loading...</div>}
    </div>
  );
}